


import { Component, Vue } from "vue-property-decorator";

import { CustomNotification, Password } from "@Internal/Models";

import { Profile } from "App";
import { Field, User } from "App/Models";
import { $t, EventBus } from "App/IOC";

import { Dictionary } from "App/Contracts";

import PhoneNumberField from "Web/Components/PhoneNumber.vue";
import Form from "Web/Components/Form/Index.vue";
import FormField from "Web/Components/Form/Field.vue";
import Fieldset from "Web/Components/Form/Fieldset.vue";
import PasswordChecklist from "Web/Components/Common/PasswordChecklist.vue";

@Component({
    components: {
        PhoneNumberField,
        Form,
        Field: FormField,
        Fieldset,
        PasswordChecklist
    }
})
export default class ProfileGeneral extends Vue {
    private user?: User;
    private loading: boolean = true;

    private fields: Dictionary<Field> = {};
    private formKey: number = 0;

    /* Inline password change (hidden until the user clicks "Change password"). */
    private changingPassword: boolean = false;
    private currentPassword: string = "";
    private newPassword: string = "";
    private confirmPassword: string = "";
    private passwordShown: boolean = false;
    private passwordError: string = "";
    private savingPassword: boolean = false;
    private newPasswordFocused: boolean = false;

    private get initials(): string {
        return this.user !== undefined ? this.user.Initials : "";
    }

    private get displayName(): string {
        if( this.user === undefined )
            return "";

        const name = [ this.user.FirstName, this.user.LastName ]
            .filter( part => part !== undefined && part !== "" )
            .join( " " );

        return name !== "" ? name : ( this.user.Email || "" );
    }

    /* New password must satisfy the shared policy and match the confirmation. */
    private get newPasswordError(): string | undefined {
        if( this.newPassword === "" )
            return undefined;

        const invalid = new Password( this.newPassword ).Validate();

        if( invalid !== undefined )
            return $t( invalid.Message );

        if( this.confirmPassword !== "" && this.newPassword !== this.confirmPassword )
            return $t( "PROFILE_passwordMismatch" );

        return undefined;
    }

    private get canChangePassword(): boolean {
        return this.currentPassword !== ""
            && this.newPassword !== ""
            && this.confirmPassword !== ""
            && this.newPasswordError === undefined
            && !this.savingPassword;
    }

    private async created(): Promise<void> {
        this.fields = {
            FirstName: Field.Text( "FirstName", $t( "CONSOLE_USERS_fieldFirstName" ) ),
            LastName: Field.Text( "LastName", $t( "CONSOLE_USERS_fieldLastName" ) ),
            Email: Field.Text( "Email", $t( "CONSOLE_USERS_fieldEmail" ) ),
            Phone: Field.Text( "Phone", $t( "CONSOLE_USERS_fieldPhone" ) )
        };

        await this.load();
    }

    private async load(): Promise<void> {
        const [ user, error ] = await Profile.Get();

        /* On failure render the form with an empty record (and drop the spinner) instead of a blank
           page; the error is surfaced separately. */
        if( error !== undefined || user === undefined ) {
            if( error !== undefined )
                EventBus.Publish( "Error", error );

            this.user = new User( 0, "" );
            this.loading = false;
            return;
        }

        this.user = user;
        this.loading = false;
    }

    private async save( record: User ): Promise<void> {
        if( this.user === undefined )
            return;

        const error = await Profile.Update({
            FirstName: record.FirstName,
            LastName: record.LastName,
            Phone: record.Phone
        });

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.notify( $t( "PROFILE_saved" ) );

        await this.load();
        this.formKey++;
    }

    private openPasswordChange(): void {
        this.changingPassword = true;
        this.passwordError = "";
    }

    private cancelPasswordChange(): void {
        this.changingPassword = false;
        this.currentPassword = "";
        this.newPassword = "";
        this.confirmPassword = "";
        this.passwordError = "";
    }

    private async submitPasswordChange(): Promise<void> {
        if( !this.canChangePassword )
            return;

        this.savingPassword = true;
        this.passwordError = "";

        const error = await Profile.ChangePassword(
            new Password( this.currentPassword ),
            new Password( this.newPassword )
        );

        this.savingPassword = false;

        if( error !== undefined ) {
            this.passwordError = error.Message;
            return;
        }

        this.cancelPasswordChange();
        this.notify( $t( "PROFILE_passwordChanged" ) );
    }

    private notify( message: string ): void {
        EventBus.Publish( "Notification", CustomNotification.Success(
            $t( "PROFILE_title" ), message
        ));
    }
}
